import { Component } from '@theme/component';

/**
 * Ola · WhatsApp flotante (sections/ola-whatsapp.liquid).
 *
 * El saludo: a los `data-delay` segundos aparece el globo, primero «escribiendo…» y luego el texto
 * (sin animaciones si la persona las tiene reducidas, directo el texto). Se va solo a los 12
 * segundos. Se muestra una vez por visita (`sessionStorage`); con la X no vuelve en tres días
 * (`localStorage`). Al abrir WhatsApp desde el globo o el botón, el globo se cierra.
 * En el editor del tema el saludo aparece siempre y no se va solo, para poder verlo.
 *
 * El estado del globo va en `data-greeting` del elemento: `typing`, `message` o `closing`.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [greeting] - El globo del saludo (no está si el saludo está apagado).
 * @property {HTMLElement} [button] - El botón redondo.
 *
 * @extends {Component<Refs>}
 */
export class OlaWhatsapp extends Component {
  /** @type {number[]} */
  #timers = [];

  connectedCallback() {
    super.connectedCallback();
    this.#schedule();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#clearTimers();
    this.#schedule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearTimers();
  }

  /** Cierra el saludo con la X y no lo vuelve a mostrar por unos días. */
  dismiss() {
    if (!this.#preview) write(window.localStorage, DISMISSED_KEY, String(Date.now()));
    this.#close();
    this.refs.button?.focus();
  }

  /** Al abrir WhatsApp, el saludo ya no hace falta en esta visita. */
  opened() {
    if (!this.#preview) write(window.sessionStorage, SEEN_KEY, '1');
    if (this.dataset.greeting) this.#close();
  }

  get #preview() {
    return this.hasAttribute('data-design-mode');
  }

  #schedule() {
    if (!this.refs.greeting) return;
    const delay = Number(this.dataset.delay ?? 4);
    if (this.#preview) {
      this.#later(() => this.#open(), 600);
      return;
    }
    if (!(delay > 0) || recentlyDismissed() || read(window.sessionStorage, SEEN_KEY)) return;
    this.#later(() => this.#open(), delay * 1000);
  }

  #open() {
    const { greeting } = this.refs;
    if (!greeting) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    greeting.hidden = false;
    this.dataset.greeting = reduced ? 'message' : 'typing';
    if (!reduced) this.#later(() => (this.dataset.greeting = 'message'), TYPING_MS);

    if (!this.#preview) {
      write(window.sessionStorage, SEEN_KEY, '1');
      this.#later(() => this.#close(), VISIBLE_MS);
    }
  }

  #close() {
    const { greeting } = this.refs;
    this.#clearTimers();
    if (!greeting || greeting.hidden) return;

    const finish = () => {
      greeting.hidden = true;
      delete this.dataset.greeting;
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }
    this.dataset.greeting = 'closing';
    this.#later(finish, CLOSING_MS);
  }

  /**
   * @param {() => void} callback
   * @param {number} ms
   */
  #later(callback, ms) {
    this.#timers.push(window.setTimeout(callback, ms));
  }

  #clearTimers() {
    for (const timer of this.#timers) window.clearTimeout(timer);
    this.#timers = [];
  }
}

const SEEN_KEY = 'ola-wa-saludo-visto';
const DISMISSED_KEY = 'ola-wa-saludo-cerrado';
const DISMISSED_DAYS = 3;
const TYPING_MS = 1400;
const VISIBLE_MS = 12_000;
const CLOSING_MS = 250;

function recentlyDismissed() {
  const at = Number(read(window.localStorage, DISMISSED_KEY));
  return at > 0 && Date.now() - at < DISMISSED_DAYS * 86_400_000;
}

/**
 * El almacenamiento puede no estar disponible (navegación privada, bloqueado): sin él, el saludo
 * se comporta como en una visita nueva.
 * @param {Storage} storage
 * @param {string} key
 */
function read(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {Storage} storage
 * @param {string} key
 * @param {string} value
 */
function write(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch {
    // Sin almacenamiento, no pasa nada.
  }
}

if (!customElements.get('ola-whatsapp')) {
  customElements.define('ola-whatsapp', OlaWhatsapp);
}
